import React from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ShieldAlert, LayoutDashboard, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";

export function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, isAdmin, adminLoading, signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/auth");
    } catch (err) {
      console.error("[AdminRoute] Error during sign out:", err);
    }
  };

  // 1. Auth Loading State
  if (loading) {
    return (
      <div className="flex min-h-[60vh] w-full max-w-full items-center justify-center px-4">
        <div className="flex max-w-md flex-col items-center text-center animate-in fade-in duration-300">
          <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-purple-100 border-t-purple-600 mb-4" />
          <span className="text-sm font-semibold text-slate-600 tracking-wide animate-pulse">
            Carregando sessão...
          </span>
        </div>
      </div>
    );
  }

  // 2. Unauthenticated State
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Emergency local check for immediate access for core administrators
  const userEmail = user.email?.trim().toLowerCase();
  const isEmergencyAdmin = userEmail === "luciogiorpicanco@gmail.com" || userEmail === "lucioigorpicanco@gmail.com";

  // 3. Admin Loading State (Only if not emergency admin)
  if (adminLoading && !isEmergencyAdmin) {
    return (
      <div className="flex min-h-[60vh] w-full max-w-full items-center justify-center px-4">
        <div className="flex max-w-md flex-col items-center text-center animate-in fade-in duration-300">
          <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-purple-100 border-t-purple-600 mb-4" />
          <span className="text-sm font-semibold text-slate-600 tracking-wide animate-pulse">
            Verificando permissões de administrador...
          </span>
        </div>
      </div>
    );
  }

  // 4. Unauthorized State
  const isAuthorized = isEmergencyAdmin || isAdmin;

  if (!isAuthorized) {
    return (
      <div className="flex min-h-[80vh] items-center justify-center p-4">
        <div className="w-full max-w-md overflow-hidden rounded-3xl border border-red-100 bg-white p-8 text-center shadow-2xl transition-all hover:shadow-3xl animate-in zoom-in-95 duration-300">
          
          {/* Animated restricted shield icon */}
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-50 text-red-500 shadow-inner animate-bounce">
            <ShieldAlert className="h-8 w-8" />
          </div>

          <h2 className="mt-6 text-2xl font-black tracking-tight text-gray-900">
            Acesso Restrito
          </h2>
          
          <p className="mt-3 text-sm font-medium leading-relaxed text-gray-500">
            Esta área é exclusiva para administradores. Seu e-mail <code className="font-mono text-xs bg-gray-100 text-red-600 px-1.5 py-0.5 rounded-md font-bold">{user.email}</code> não está registrado no grupo administrativo.
          </p>

          <div className="mt-8 flex flex-col gap-3">
            <Button
              onClick={() => navigate("/dashboard")}
              className="w-full h-11 bg-primary hover:bg-primary/95 text-white font-bold rounded-xl text-xs flex items-center justify-center gap-2 shadow-md transition-all"
            >
              <LayoutDashboard className="w-4 h-4" />
              Voltar ao Dashboard
            </Button>
            
            <Button
              variant="outline"
              onClick={handleSignOut}
              className="w-full h-11 border-gray-200 hover:bg-gray-50 text-gray-500 font-bold rounded-xl text-xs flex items-center justify-center gap-2 transition-all"
            >
              <LogOut className="w-4 h-4 text-gray-400" />
              Entrar com outra conta
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // 5. Authorized State
  return <>{children}</>;
}
